import dayjs, { type ConfigType } from 'dayjs';

/**
 * ISO-8601 with the local timezone offset, e.g. `2026-07-07T08:00:00+05:30`.
 * The dayjs `Z` token renders the offset (`+05:30`), so the API always receives
 * an unambiguous instant instead of a timezone-less wall-clock string.
 */
const API_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
const API_DATETIME_MS_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

/**
 * Serialize an exact instant (from a Dayjs, Date, or parseable value) for the API.
 * ALWAYS includes the local timezone offset. Use this for any date/datetime sent
 * to the backend — the backend stores every date as a timezone-aware instant, so a
 * naive value would be interpreted against the server's timezone and drift.
 */
export const toApiDateTime = (value: ConfigType): string => dayjs(value).format(API_DATETIME_FORMAT);

/**
 * Start of the given value's local calendar day (00:00:00), offset-qualified.
 * Use for the lower bound of a "filter by day" range so the API filters the user's
 * local day rather than a UTC day.
 */
export const toApiDayStart = (value: ConfigType): string => dayjs(value).startOf('day').format(API_DATETIME_FORMAT);

/**
 * End of the given value's local calendar day (23:59:59.999), offset-qualified.
 * Use for the inclusive upper bound of a "filter by day" range.
 */
export const toApiDayEnd = (value: ConfigType): string => dayjs(value).endOf('day').format(API_DATETIME_MS_FORMAT);
